import { useEffect, useState } from 'react';
import { FileText, Save, Check, ExternalLink, Eye } from 'lucide-react';
import { ADMIN_TIME_ZONE } from '@/lib/format/admin-time';
import { adminFetch } from '@/api';

interface DynamicPage {
  slug: string;
  title: string;
  content: string;
  updatedAt: string;
  updatedBy: string | null;
}

const PAGE_META: Record<string, { label: string; description: string }> = {
  'privacy-policy': { label: 'Privacy Policy', description: 'App privacy policy shown to users' },
  'terms': { label: 'Terms & Conditions', description: 'App terms of service' },
  'support': { label: 'Support', description: 'Help & support page with contact info' },
};

export default function PagesPage() {
  const [pages, setPages] = useState<DynamicPage[]>([]);
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchPages = async () => {
    const res = await adminFetch('/api/pages');
    const data = await res.json();
    setPages(data.pages);
    if (!activeSlug && data.pages.length > 0) {
      selectPage(data.pages[0]);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  const selectPage = (page: DynamicPage) => {
    setActiveSlug(page.slug);
    setEditTitle(page.title);
    setEditContent(page.content);
    setSaved(false);
    setShowPreview(false);
  };

  const savePage = async () => {
    if (!activeSlug) return;
    setSaving(true);
    setSaved(false);
    await adminFetch('/api/pages', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: activeSlug, title: editTitle, content: editContent }),
    });
    setSaving(false);
    setSaved(true);
    fetchPages();
    setTimeout(() => setSaved(false), 3000);
  };

  const activePage = pages.find(p => p.slug === activeSlug);
  const meta = activeSlug ? PAGE_META[activeSlug] : null;

  // Simple markdown preview
  const renderPreview = (md: string) => {
    return md
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold text-white mt-5 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold text-white mt-6 mb-2">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold text-white mt-0 mb-3">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-zinc-200 font-semibold">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-emerald-400 underline">$1</a>')
      .replace(/^- (.+)$/gm, '<li class="ml-4 text-zinc-400 text-[13px] mb-1">• $1</li>')
      .replace(/\n\n/g, '</p><p class="text-[13px] text-zinc-400 leading-relaxed mb-3">')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[22px] font-bold text-white tracking-tight">Pages</h1>
        <p className="text-zinc-500 text-[13px] mt-0.5">Manage privacy policy, terms, and support content</p>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* Page list */}
        <div className="space-y-1.5">
          {pages.map(page => {
            const isActive = page.slug === activeSlug;
            const pmeta = PAGE_META[page.slug];
            return (
              <button
                key={page.slug}
                onClick={() => selectPage(page)}
                className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-emerald-500/10 border border-emerald-500/20'
                    : 'bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04]'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <FileText className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-400' : 'text-zinc-600'}`} />
                  <div className="min-w-0">
                    <p className={`text-[13px] font-medium truncate ${isActive ? 'text-emerald-400' : 'text-zinc-300'}`}>
                      {pmeta?.label || page.title}
                    </p>
                    <p className="text-[10px] text-zinc-600 truncate">{pmeta?.description || page.slug}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Editor */}
        {activeSlug && activePage ? (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
            {/* Editor Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <input
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  className="text-[15px] font-semibold text-white bg-transparent border-none outline-none"
                />
                <span className="text-[10px] text-zinc-600 font-[family-name:var(--font-jetbrains)]">/{activeSlug}</span>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`/p/${activeSlug}`}
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-all"
                >
                  <ExternalLink className="w-3 h-3" />
                  Open
                </a>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                    showPreview ? 'bg-white/[0.06] text-white' : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                  }`}
                >
                  <Eye className="w-3 h-3" />
                  Preview
                </button>
                <button
                  onClick={savePage}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-500 text-black text-[11px] font-semibold hover:bg-emerald-400 disabled:opacity-40 transition-all"
                >
                  {saved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
                  {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
                </button>
              </div>
            </div>

            {showPreview ? (
              /* Preview */
              <div className="p-6 max-h-[600px] overflow-y-auto">
                <div
                  dangerouslySetInnerHTML={{ __html: `<p class="text-[13px] text-zinc-400 leading-relaxed mb-3">${renderPreview(editContent)}</p>` }}
                />
              </div>
            ) : (
              /* Textarea Editor */
              <div className="relative">
                <textarea
                  value={editContent}
                  onChange={e => { setEditContent(e.target.value); setSaved(false); }}
                  className="w-full min-h-[500px] p-5 bg-transparent text-[13px] text-zinc-300 font-[family-name:var(--font-jetbrains)] leading-relaxed resize-none outline-none placeholder:text-zinc-700"
                  placeholder="Write your page content using Markdown..."
                  spellCheck={false}
                />
                {/* Markdown hint */}
                <div className="absolute bottom-3 right-3 flex items-center gap-3 text-[10px] text-zinc-700">
                  <span># Heading</span>
                  <span>**bold**</span>
                  <span>*italic*</span>
                  <span>[link](url)</span>
                  <span>- list</span>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="px-5 py-2.5 border-t border-white/[0.04] flex items-center justify-between">
              <p className="text-[10px] text-zinc-700">
                Public URL: <span className="text-zinc-500 font-[family-name:var(--font-jetbrains)]">/p/{activeSlug}</span>
              </p>
              {activePage.updatedBy && (
                <p className="text-[10px] text-zinc-700">
                  Last edited by {activePage.updatedBy} · {new Date(activePage.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: ADMIN_TIME_ZONE })}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-20">
            <p className="text-zinc-600 text-sm">Select a page to edit</p>
          </div>
        )}
      </div>
    </div>
  );
}
